// Main game file for Space Invaders
import { AudioManager } from './audio.js';
import { Controls } from './controls.js';
import { GameState } from './game-state.js';
import { Renderer } from './renderer.js';
import { UI } from './ui.js';

export class Game {
  constructor() {
    this.canvas = document.getElementById('gameCanvas');
    this.audio = new AudioManager();
    this.controls = new Controls();
    this.gameState = new GameState(this.canvas);
    this.renderer = new Renderer(this.canvas);
    this.ui = new UI();
    
    this.gameState.setControls(this.controls);
    this.setupEventListeners();
    this.setupAutoShoot();
    this.ui.startHUDAutoUpdate(this.gameState);
  }

  setupEventListeners() {
    // Mute button
    this.ui.setMuteButtonCallback(() => {
      const isMuted = this.audio.toggleMute();
      this.ui.updateMuteButton(isMuted);
    });
    
    // Restart button
    this.ui.setRestartButtonCallback(() => {
      this.startGame();
    });
  }

  setupAutoShoot() {
    // Auto-fire friendly accessible toggle: hold space to auto-shoot every cooldown
    const autoShootLoop = () => {
      if (this.gameState && this.gameState.running && this.controls.isShooting()) {
        if (this.gameState.player.cooldown <= 0) {
          this.gameState.playerShoot();
          this.audio.shoot();
        }
      }
      requestAnimationFrame(autoShootLoop);
    };
    autoShootLoop();
  }

  startGame() {
    this.gameState.reset();
    this.audio.startGame();
    this.gameLoop();
  }

  gameLoop(ts) {
    if (!this.gameState.lastTime) {
      this.gameState.lastTime = ts || performance.now();
    }
    
    const now = ts || performance.now();
    const dt = Math.min(0.05, (now - this.gameState.lastTime) / 1000);
    this.gameState.lastTime = now;

    if (this.gameState.running && this.gameState.barriers) {
      this.update(dt);
    }
    
    this.renderer.render(this.gameState);
    this.ui.updateHUD(this.gameState);
    
    requestAnimationFrame((ts) => this.gameLoop(ts));
  }

  update(dt) {
    if (!this.gameState.barriers) return;
    
    // Store previous state for sound effects
    const prevEnemyCount = this.gameState.getAliveEnemies().length;
    const prevLives = this.gameState.lives;
    const wasLevelComplete = this.gameState.isLevelComplete();
    
    // Reset sound flags
    this.gameState.barrierHit = false;
    this.gameState.enemyBarrierHit = false;
    this.gameState.enemyShoot = false;
    
    this.gameState.update(dt);
    
    // Play sound effects based on state changes
    this.playSoundEffects(prevEnemyCount, prevLives, wasLevelComplete);
  }

  playSoundEffects(prevEnemyCount, prevLives, wasLevelComplete) {
    if (!this.gameState.barriers) return;
    
    const currentEnemyCount = this.gameState.getAliveEnemies().length;
    
    // Enemy hit
    if (currentEnemyCount < prevEnemyCount) {
      this.audio.enemyHit();
      
      // Sjekk for ny highscore
      if (this.gameState.updateHighscore()) {
        // Ny highscore! Kan legge til spesiell lydeffekt her senere
        console.log('Ny highscore!', this.gameState.highscore);
      }
    }
    
    // Player hit - med dramatiske effekter
    if (this.gameState.lives < prevLives) {
      this.audio.playerHit();
      // Trigger dramatiske visuelle effekter
      this.ui.triggerLifeLostEffect();
    }
    
    // Level complete
    if (!wasLevelComplete && this.gameState.isLevelComplete()) {
      this.audio.levelComplete();
    }
    
    // Game over
    if (this.gameState.gameOver) {
      this.audio.gameOver();
    }
    
    // Barrier hits
    if (this.gameState.barrierHit) {
      this.audio.barrierHit();
    }
    
    if (this.gameState.enemyBarrierHit) {
      this.audio.enemyBarrierHit();
    }
    
    // Enemy shoot
    if (this.gameState.enemyShoot) {
      this.audio.enemyShoot();
    }
  }
}

// Initialize game when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const game = new Game();
  
  // Start game automatically since no background music
  setTimeout(() => {
    game.startGame();
  }, 100);
});
